import React from 'react';
import {
  SkeletonAvatar,
  SkeletonButton,
  SkeletonCircle,
  SkeletonLine,
  SkeletonPill,
} from './Skeleton';

const DEFAULT_COLUMNS = Object.freeze([{ width: '70%' }, { width: '84%' }, { width: '58%' }]);

const normalizeCount = (count, fallback = 4) => Math.max(1, Number(count) || fallback);

const renderCellSkeleton = (column) => {
  const width = column.width || '72%';
  const height = column.height;
  const className = column.skeletonClassName || '';

  switch (column.variant) {
    case 'profile':
      return (
        <span className="table-skeleton-profile">
          <SkeletonAvatar className={className} size={column.size || 34} />
          <span className="table-skeleton-stack">
            <SkeletonLine width={column.primaryWidth || '70%'} height={column.primaryHeight || 12} />
            <SkeletonLine width={column.secondaryWidth || '44%'} height={column.secondaryHeight || 10} />
          </span>
        </span>
      );
    case 'stack':
      return (
        <span className="table-skeleton-stack">
          <SkeletonLine width={column.primaryWidth || width} height={column.primaryHeight || height || 12} />
          <SkeletonLine width={column.secondaryWidth || '58%'} height={column.secondaryHeight || 10} />
        </span>
      );
    case 'avatar':
      return <SkeletonAvatar className={className} size={column.size || 32} />;
    case 'circle':
      return <SkeletonCircle className={className} size={column.size || 18} />;
    case 'pill':
      return <SkeletonPill className={className} width={width} height={height} />;
    case 'button':
      return <SkeletonButton className={className} width={width} height={height} />;
    case 'actions': {
      const widths = Array.isArray(column.widths) && column.widths.length > 0
        ? column.widths
        : [72, 72, 72];
      return (
        <span className="table-skeleton-actions">
          {widths.map((actionWidth, index) => (
            <SkeletonButton
              key={`table-skeleton-action-${index}`}
              className={className}
              width={actionWidth}
              height={height || 30}
            />
          ))}
        </span>
      );
    }
    default:
      return <SkeletonLine className={className} width={width} height={height} />;
  }
};

export default function TableSkeletonRows({
  rows = 4,
  columns = DEFAULT_COLUMNS,
  rowClassName = '',
}) {
  const normalizedRows = Array.from({ length: normalizeCount(rows) }, (_, index) => index);
  const normalizedColumns = Array.isArray(columns) && columns.length > 0 ? columns : DEFAULT_COLUMNS;

  return (
    <>
      {normalizedRows.map((rowIndex) => (
        <tr
          key={`table-skeleton-row-${rowIndex}`}
          className={['table-skeleton-row', rowClassName].filter(Boolean).join(' ')}
          aria-hidden="true"
        >
          {normalizedColumns.map((column, columnIndex) => (
            <td
              key={`table-skeleton-cell-${rowIndex}-${column.key || columnIndex}`}
              className={[
                'table-skeleton-cell',
                column.align === 'right' ? 'table-skeleton-cell--right' : '',
                column.align === 'center' ? 'table-skeleton-cell--center' : '',
                column.className || '',
              ].filter(Boolean).join(' ')}
            >
              {renderCellSkeleton(column)}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
